// Ack / Silence controls for one alarm (Phase UI-2). TWO distinct buttons —
// never merged: Acknowledge (canAck) and Silence (temporary; canSilence). Each
// POSTs a typed command to the boat with a per-click Idempotency-Key; the boat
// stays authoritative, so there is NO optimistic state — on 2xx we just re-read
// the snapshot (the WS `alert` frame confirms moments later). An unreachable
// boat is an honest inline banner (the cloud never fabricates an acknowledged
// state), reusing the exact `boat_offline` detection the reveal relay uses.
//
// Rendered both in the Active band rows and on the alarm detail page.

import { useState } from 'react';

import { api } from '../../api';
import {
  alarmCommandDisabledTitle,
  alarmCommandErrorMessage,
  alarmCommandLabel,
  alarmCommandOfflineMessage,
  alarmCommandPendingLabel,
  canAck,
  canSilence,
  type AlarmCommandAction,
  type AlarmSeverity,
  type AlertState,
} from '../../lib/alarmDisplay';
import { isBoatOfflineRelayError } from '../../lib/revealStaging';

interface AckSilenceButtonsProps {
  boatId: string;
  /** The runtime alert id (== definition id). */
  alertId: string;
  /** The alert's current lifecycle state — gates which buttons appear. */
  state: AlertState;
  /**
   * The definition's severity — the temporary-silence period derives from it,
   * so the button can name its duration. Absent (a row whose definition carries
   * no severity) keeps the generic "Silence (temporary)".
   */
  severity?: AlarmSeverity | null;
  /** Presence says the boat is unreachable → buttons disabled with a tooltip. */
  boatOffline: boolean;
  /** Called after a 2xx relay so the owner surface re-reads the snapshot. */
  onActionComplete: () => void;
}

export function AckSilenceButtons({
  boatId,
  alertId,
  state,
  severity,
  boatOffline,
  onActionComplete,
}: AckSilenceButtonsProps) {
  const [busy, setBusy] = useState<AlarmCommandAction | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  // A Try it guest silences and acknowledges its own slot's alarms too (cloud ADR 0444).
  const showAck = canAck(state);
  const showSilence = canSilence(state);
  // Nothing actionable in this state (Normal / Acknowledged / Disabled).
  if (!showAck && !showSilence) return null;

  async function run(action: AlarmCommandAction): Promise<void> {
    if (busy !== null) return;
    setBusy(action);
    setBanner(null);
    try {
      await api(`/v1/boats/${boatId}/alerts/${alertId}/${action}`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
      });
      // The boat executes it on its own evaluator and mirrors the resulting
      // transition back over the alerts WS lane — refresh to pick that up.
      onActionComplete();
    } catch (err) {
      setBanner(
        isBoatOfflineRelayError(err)
          ? alarmCommandOfflineMessage(action)
          : alarmCommandErrorMessage(action)
      );
    } finally {
      setBusy(null);
    }
  }

  const disabledTitle = boatOffline ? alarmCommandDisabledTitle() : undefined;
  const disabled = boatOffline || busy !== null;

  return (
    <div className="alarm-actions" data-testid={`alarm-actions-${alertId}`}>
      <div className="alarm-actions__row">
        {showAck ? (
          <button
            type="button"
            className="alarm-btn alarm-btn--ack"
            disabled={disabled}
            title={disabledTitle}
            data-testid={`alarm-ack-${alertId}`}
            onClick={() => void run('ack')}
          >
            {busy === 'ack' ? alarmCommandPendingLabel('ack') : alarmCommandLabel('ack')}
          </button>
        ) : null}
        {showSilence ? (
          <button
            type="button"
            className="alarm-btn alarm-btn--silence"
            disabled={disabled}
            title={disabledTitle}
            data-testid={`alarm-silence-${alertId}`}
            onClick={() => void run('silence')}
          >
            {busy === 'silence'
              ? alarmCommandPendingLabel('silence')
              : alarmCommandLabel('silence', severity)}
          </button>
        ) : null}
      </div>
      {banner !== null ? (
        <p
          className="alarm-command-banner"
          role="alert"
          data-testid={`alarm-command-banner-${alertId}`}
        >
          {banner}
        </p>
      ) : null}
    </div>
  );
}
